import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { resolveCompany } from "@/lib/company-access";

/**
 * What is waiting on this user right now.
 *
 * Every cross-company flow used to depend on an email arriving and being read.
 * Emails get missed, filtered and ignored, and a deal that stalls because nobody
 * saw a message looks exactly like a product that does not work. These counts
 * put the same information inside the app, where the work actually happens.
 *
 * Deliberately cheap: count-only queries, every one of them failure-tolerant, so
 * a missing table or an unapplied migration can never break the shell.
 */
export const getPendingActions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const empty = { contracts: 0, deals: 0, opportunities: 0, radar: 0, total: 0 };
    const company = await resolveCompany(context.supabase, context.userId);
    if (!company) return empty;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const safeCount = async (fn: () => Promise<number>): Promise<number> => {
      try {
        return await fn();
      } catch {
        return 0;
      }
    };

    const [contracts, deals, opportunities, radar] = await Promise.all([
      // Contracts I am party to, still open, that I have not signed.
      safeCount(async () => {
        const { data: rows } = await (supabaseAdmin as any)
          .from("contracts")
          .select("id, company_id, counterpart_company_id, status")
          .or(`company_id.eq.${company.id},counterpart_company_id.eq.${company.id}`)
          .in("status", ["draft", "sent"]);
        if (!rows?.length) return 0;
        const ids = rows.map((r: any) => r.id);
        const { data: sigs } = await (supabaseAdmin as any)
          .from("contract_signatures")
          .select("contract_id, role")
          .in("contract_id", ids);
        const signed = new Set((sigs ?? []).map((s: any) => `${s.contract_id}:${s.role}`));
        return rows.filter((r: any) => {
          const myRole = r.company_id === company.id ? "provider" : "customer";
          return !signed.has(`${r.id}:${myRole}`);
        }).length;
      }),

      // Deals awaiting my response or my signature.
      safeCount(async () => {
        const { data: rows } = await (supabaseAdmin as any)
          .from("deals")
          .select("id, owner_a, owner_b, status, signed_a, signed_b")
          .or(`owner_a.eq.${context.userId},owner_b.eq.${context.userId}`)
          .in("status", ["proposed", "accepted"]);
        return (rows ?? []).filter((d: any) => {
          const isA = d.owner_a === context.userId;
          // A proposal is the counterpart's move; an accepted deal needs my
          // signature if I have not given it yet.
          if (d.status === "proposed") return !isA;
          return isA ? !d.signed_a : !d.signed_b;
        }).length;
      }),

      safeCount(async () => {
        const { count } = await (supabaseAdmin as any)
          .from("opportunities")
          .select("id", { count: "exact", head: true })
          .eq("company_id", company.id)
          .eq("status", "new");
        return count ?? 0;
      }),

      safeCount(async () => {
        const { count } = await (supabaseAdmin as any)
          .from("radar_signals")
          .select("id", { count: "exact", head: true })
          .eq("company_id", company.id)
          .eq("status", "new");
        return count ?? 0;
      }),
    ]);

    return {
      contracts,
      deals,
      opportunities,
      radar,
      // Only counts that represent an actual obligation. Opportunities and radar
      // signals are suggestions — worth a badge, but not "someone is waiting".
      total: contracts + deals,
    };
  });
